import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { get_base_url } from '@/shared/http/client';
import { clearCachedAvailableProviders } from '@/app/providers/globalState';

const AuthContext = createContext(null);

/** Where `POST /users/bootstrap` strangers (`state: "no_access"`) are sent on SaaS. */
export const WAITLIST_APPLY_PATH = '/waitlist/apply';

/**
 * Where strangers are sent on a self-hosted install, which has no waiting
 * list: a "no access" page that only offers "Log out" (a workspace admin has
 * to invite them).
 */
export const NO_ACCESS_PATH = '/request-access';

/**
 * Pick the landing for a signed-in person with no workspace access.
 *
 * `waitlistEnabled` is the backend's `waitlist_enabled` (`IS_SAAS`) flag, so
 * the choice does not depend on the order in which `/auth/debug` and
 * `/users/bootstrap` resolve.  Anything but an explicit `true` keeps the
 * person out, mirroring the backend, which answers 404 on every waiting-list
 * route when `IS_SAAS` is off.
 */
export function resolveNoAccessPath(waitlistEnabled) {
    return waitlistEnabled === true ? WAITLIST_APPLY_PATH : NO_ACCESS_PATH;
}

function getAuthBaseUrl() {
    const isProxy = window.location.port === '8760';
    return isProxy ? '/api' : get_base_url(window.location.origin);
}

/**
 * Overlay the persisted profile from `POST /users/bootstrap` on the session
 * user: the stored (data-URL) picture wins over a provider URL, and the stored
 * display name wins so a name edited in the onboarding wizard survives
 * re-login.  Returns `null` when nothing needs to change.
 */
export function mergeBootstrapUser(sessionUser, bootUser) {
    if (!bootUser) return null;
    const base = sessionUser || {};
    const merged = { ...base };
    let changed = false;
    const persistedPic = bootUser.picture;
    if (persistedPic && (!base.picture || !String(base.picture).startsWith('data:'))) {
        merged.picture = persistedPic;
        changed = true;
    }
    const persistedName = typeof bootUser.name === 'string' ? bootUser.name.trim() : '';
    if (persistedName && persistedName !== base.name) {
        merged.name = persistedName;
        changed = true;
    }
    // Onboarding answers live only on the user document, so they always come
    // from the bootstrap (the IdP session knows nothing about them).
    const persistedCountry = typeof bootUser.country === 'string' ? bootUser.country.trim() : '';
    if (persistedCountry && persistedCountry !== base.country) {
        merged.country = persistedCountry;
        changed = true;
    }
    if (bootUser.onboarding && typeof bootUser.onboarding === 'object' && bootUser.onboarding !== base.onboarding) {
        merged.onboarding = bootUser.onboarding;
        changed = true;
    }
    if (typeof bootUser.picture_set_by_user === 'boolean' && bootUser.picture_set_by_user !== base.picture_set_by_user) {
        merged.picture_set_by_user = bootUser.picture_set_by_user;
        changed = true;
    }
    return changed ? merged : null;
}

/** Centered spinner/error in the main content area (nav stays visible). */
function AuthGateContent({ children }) {
    return (
        <div
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            {children}
        </div>
    );
}

function AuthLoadingContent() {
    return (
        <AuthGateContent>
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '50vh',
                }}
            >
                <Spin size="large" />
            </div>
        </AuthGateContent>
    );
}

function AuthErrorContent({ message }) {
    return (
        <AuthGateContent>
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    minHeight: '50vh',
                }}
            >
                <Alert
                    type="error"
                    showIcon
                    message="Could not verify authentication"
                    description={message}
                    style={{ maxWidth: 480, width: '100%' }}
                />
            </div>
        </AuthGateContent>
    );
}

async function readAuthErrorMessage(response, fallback) {
    try {
        const data = await response.json();
        return data?.detail || data?.message || fallback;
    } catch (_) {
        return fallback;
    }
}

/** Message shown when `POST /users/bootstrap` fails for a signed-in user. */
export const BOOTSTRAP_FAILED_MESSAGE = 'Could not load your account. Check your connection and try again.';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // `false` while a full `bootstrapAuth` is in flight (the first one and
    // every `retryAuth`).  `loading` also flips on every later `refresh()`,
    // so the full-screen boundary (`AuthBoundary`) keys off this flag instead
    // and only the in-shell `AuthGate` spinner shows for mid-session refreshes.
    const [initialized, setInitialized] = useState(false);
    const [authError, setAuthError] = useState(null);
    // Client-side redirect requested by the bootstrap (stranger → waiting
    // list, creator → onboarding wizard, ...).  Consumed by the effect below
    // through the router instead of `window.location.href`, so the shell is
    // never mounted for a page the person is about to leave and nothing
    // flashes before the redirect.
    const [redirectTo, setRedirectTo] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    // `bootstrapUserSession` is a zero-dependency callback that resolves long
    // after it was created, so it reads the current path through a ref.
    const pathRef = useRef(location.pathname);
    useEffect(() => {
        pathRef.current = location.pathname;
    }, [location.pathname]);

    const requestRedirect = useCallback((path) => {
        if (pathRef.current === path) return;
        setRedirectTo(path);
    }, []);

    useEffect(() => {
        if (!redirectTo) return;
        if (location.pathname !== redirectTo) {
            // react-router applies navigations inside `startTransition`, so a
            // sync `setRedirectTo(null)` issued here would commit *before* the
            // location changes and let the old page (the shell) render once.
            // Keep the redirect pending until the router reports the new
            // path; this effect re-runs on `location.pathname` and clears it.
            navigate(redirectTo, { replace: true });
            return;
        }
        setRedirectTo(null);
    }, [redirectTo, location.pathname, navigate]);
    const [authMethod, setAuthMethod] = useState('GOOGLE');
    const [role, setRole] = useState('');
    // Platform-wide role from `POST /users/bootstrap` (god / creator / member).
    // Gates god-only UI such as the Waiting List tab; auth-disabled installs
    // behave as god, mirroring the backend's `require_god` bypass.
    const [platformRole, setPlatformRole] = useState('member');
    const [isAuthEnabled, setIsAuthEnabled] = useState(false);
    // SaaS billing flags from GET /auth/debug. Default to "self-hosted, no
    // billing" so the Plans & Billing tab (and Stripe.js) never appears
    // unless the backend explicitly opts in.
    const [isSaas, setIsSaas] = useState(false);
    // `bootstrapUserSession` is a zero-dependency callback, so it reads the
    // SaaS flag through a ref rather than the state above.
    const isSaasRef = useRef(false);
    const [billingEnabled, setBillingEnabled] = useState(false);
    const [stripePublishableKey, setStripePublishableKey] = useState(null);
    // "Give us Feedback" (avatar menu → ClickUp task) is only offered when the
    // backend has ClickUp credentials; self-hosted installs never see it.
    const [feedbackEnabled, setFeedbackEnabled] = useState(false);
    // SaaS gating: "Contact us" in the disabled task-type / autonomy tooltips
    // opens the customer-intents modal only when the backend can file the
    // request to ClickUp; otherwise the tooltip is plain text.
    const [customerIntentsEnabled, setCustomerIntentsEnabled] = useState(false);
    // Phase 4: active workspace and membership list.
    const [workspace, setWorkspace] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    // Billing plan of the active workspace ("free" | "pro"), from
    // ``/users/bootstrap``; ``null`` when billing is off or still unknown.
    // Billing is per workspace (one Stripe customer each), so the tag is the
    // same for every member of that workspace and changes on workspace switch.
    const [plan, setPlan] = useState(null);

    useEffect(() => {
        if (!window.FS || !user) {
            return;
        }

        window.FS('setIdentity', {
            uid: user?.sub || 'no sub',
            properties: {
                display_name: user?.name,
            },
        });
    }, [user]);

    /**
     * Where a signed-in person without an active workspace goes, by platform
     * role: gods to the console, creators to the onboarding wizard, members
     * to the waiting list (SaaS) or the "no access" page (self-hosted).
     */
    const routeWithoutWorkspace = useCallback((platformRoleValue) => {
        if (platformRoleValue === 'god') {
            requestRedirect('/admin');
        } else if (platformRoleValue === 'creator') {
            requestRedirect('/create-workspace');
        } else {
            requestRedirect(resolveNoAccessPath(isSaasRef.current));
        }
    }, [requestRedirect]);

    const bootstrapUserSession = useCallback(async (base, json) => {
        setUser(json.user || null);
        let resp;
        try {
            resp = await fetch(`${base}/users/bootstrap`, { method: 'POST', credentials: 'include' });
        } catch (_) {
            throw new Error(BOOTSTRAP_FAILED_MESSAGE);
        }
        if (resp.status === 401) {
            // The session died between /auth/me and the bootstrap; treat it
            // as signed out so the gate sends the person to /login.
            setUser(null);
            return;
        }
        if (!resp.ok) {
            // A failed bootstrap used to be swallowed, leaving the shell on an
            // endless spinner.  Surface it so `AuthBoundary` can offer Retry.
            throw new Error(await readAuthErrorMessage(resp, BOOTSTRAP_FAILED_MESSAGE));
        }
        const boot = await resp.json().catch(() => null);
        if (!boot || typeof boot !== 'object') {
            throw new Error(BOOTSTRAP_FAILED_MESSAGE);
        }

        // Phase 3: stranger gate — the person authenticated with the IdP
        // but has no invitation and no approved Creator request on this
        // platform.  The OAuth callback already lands them on the waiting
        // list; this is the fallback for a stranger who typed another URL.
        // SaaS sends them to the waiting-list questionnaire; a self-hosted
        // install shows the "no access" page instead.
        if (boot.state === 'no_access') {
            setPlatformRole('member');
            const waitlistEnabled = typeof boot.waitlist_enabled === 'boolean'
                ? boot.waitlist_enabled
                : isSaasRef.current;
            requestRedirect(resolveNoAccessPath(waitlistEnabled));
            return;
        }

        // Phase 4: no workspace — route by platform role.
        if (boot.state === 'no_workspace') {
            const pr = boot.platform_role || 'member';
            setPlatformRole(pr);
            routeWithoutWorkspace(pr);
            const mergedUser = mergeBootstrapUser(json.user, boot.user);
            if (mergedUser) setUser(mergedUser);
            return;
        }

        // Onboarding in progress — a Creator started the /create-workspace
        // wizard (saved at least one step) but has not accepted the terms
        // yet.  They may already own a workspace, so keep the list around
        // for the wizard to resume on the right step.
        if (boot.state === 'onboarding') {
            setPlatformRole(boot.platform_role || 'creator');
            if (boot.workspaces) setWorkspaces(boot.workspaces);
            const mergedUser = mergeBootstrapUser(json.user, boot.user);
            if (mergedUser) setUser(mergedUser);
            requestRedirect('/create-workspace');
            return;
        }

        // Phase 4: state=ok — active workspace resolved.
        const bootRole = boot.role;
        if (bootRole) setRole(bootRole);
        const pr = boot.platform_role || boot.user?.platform_role || 'member';
        setPlatformRole(pr);
        const mergedUser = mergeBootstrapUser(json.user, boot.user);
        if (mergedUser) setUser(mergedUser);
        if (boot.workspaces) setWorkspaces(boot.workspaces);
        setPlan(boot.plan === 'free' || boot.plan === 'pro' ? boot.plan : null);

        if (!boot.workspace) {
            // Defensive: an `ok` answer without a workspace (an older backend
            // whose last workspace was deleted) must not strand the person on
            // the shell's spinner — route them exactly like `no_workspace`.
            routeWithoutWorkspace(pr);
            return;
        }
        setWorkspace(boot.workspace);

        const currentPath = pathRef.current;
        // Legacy: Unassigned members on the migrated workspace still
        // land on /pending-approval until their role is promoted.
        const isUnassigned = bootRole === 'Unassigned';
        if (isUnassigned) {
            requestRedirect('/pending-approval');
        } else if (
            currentPath === '/pending-approval'
            || currentPath === WAITLIST_APPLY_PATH
            || currentPath === NO_ACCESS_PATH
            || currentPath === '/create-workspace'
        ) {
            // User now has an active workspace (invitation consumed,
            // Creator request approved, or workspace just created);
            // redirect home.
            requestRedirect('/');
        }
    }, [requestRedirect, routeWithoutWorkspace]);

    const bootstrapAuth = useCallback(async () => {
        setLoading(true);
        // Drop back to "not initialized" so a retry from the error screen keeps
        // the neutral boundary loader up instead of mounting the route table
        // (and its fetches) while the bootstrap is still running.
        setInitialized(false);
        setAuthError(null);
        const base = getAuthBaseUrl();
        try {
            let debugRes;
            try {
                debugRes = await fetch(`${base}/auth/debug`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                });
            } catch (_) {
                throw new Error(
                    'Cannot reach the backend. Check that the API is running (proxy on :8760/api or backend on :8000).'
                );
            }

            if (!debugRes.ok) {
                const detail = await readAuthErrorMessage(
                    debugRes,
                    `Auth configuration check failed (${debugRes.status}).`
                );
                throw new Error(detail);
            }

            const info = await debugRes.json();
            const method = (info?.auth_method || 'GOOGLE').toUpperCase();
            setAuthMethod(method);
            setIsAuthEnabled(info?.auth_enabled);
            const saas = info?.is_saas === true;
            isSaasRef.current = saas;
            setIsSaas(saas);
            setBillingEnabled(info?.billing_enabled === true);
            setStripePublishableKey(info?.stripe_publishable_key || null);
            setFeedbackEnabled(info?.feedback_enabled === true);
            setCustomerIntentsEnabled(info?.customer_intents_enabled === true);
            if (method === 'NONE' || info?.auth_enabled === false) {
                setUser({ name: 'Guest', email: '' });
                setRole('Admin');
                setPlatformRole('god');
                return;
            }

            let meRes;
            try {
                meRes = await fetch(`${base}/auth/me`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                });
            } catch (_) {
                throw new Error(
                    'Cannot reach the backend. Check that the API is running (proxy on :8760/api or backend on :8000).'
                );
            }

            if (meRes.status === 401) {
                setUser(null);
                return;
            }
            if (!meRes.ok) {
                const detail = await readAuthErrorMessage(
                    meRes,
                    `Session check failed (${meRes.status}).`
                );
                throw new Error(detail);
            }

            const json = await meRes.json();
            await bootstrapUserSession(base, json);
        } catch (error) {
            setAuthError(error?.message || 'Authentication check failed.');
            setUser(null);
        } finally {
            setLoading(false);
            setInitialized(true);
        }
    }, [bootstrapUserSession]);

    const refresh = useCallback(() => {
        setLoading(true);
        setAuthError(null);
        const base = getAuthBaseUrl();
        fetch(`${base}/auth/me`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        })
            .then(async (res) => {
                if (res.status === 401) {
                    setUser(null);
                    return;
                }
                if (!res.ok) {
                    const detail = await readAuthErrorMessage(
                        res,
                        `Session check failed (${res.status}).`
                    );
                    throw new Error(detail);
                }
                const json = await res.json();
                await bootstrapUserSession(base, json);
            })
            .catch((error) => {
                if (error?.message) {
                    setAuthError(error.message);
                }
                setUser(null);
            })
            .finally(() => setLoading(false));
    }, [bootstrapUserSession]);

    useEffect(() => {
        bootstrapAuth();
    }, [bootstrapAuth]);

    const loginWithGoogle = useCallback(async (nextPath) => {
        const base = getAuthBaseUrl();
        console.log('Redirecting to Google OAuth:', `${base}/auth/google/login`);

        try {
            const debugRes = await fetch(`${base}/auth/debug`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!debugRes.ok) {
                throw new Error(`Auth configuration check failed (${debugRes.status}).`);
            }
            const debugInfo = await debugRes.json();
            if (debugInfo) {
                console.log('Auth debug info:', debugInfo);

                if (!debugInfo.auth_enabled) {
                    alert('Authentication is disabled. You can access the app without login.');
                    return;
                }

                const providers = Array.isArray(debugInfo.enabled_providers)
                    ? debugInfo.enabled_providers
                    : [];
                const googleEnabled = providers.length
                    ? providers.includes('GOOGLE')
                    : (debugInfo.auth_method === 'GOOGLE' || debugInfo.auth_method === 'BOTH');
                if (!googleEnabled) {
                    alert(`Auth method is set to ${debugInfo.auth_method}.`);
                    return;
                }

                if (!debugInfo.google_client_id_configured) {
                    alert('Google OAuth is not configured. Please check the backend configuration.');
                    return;
                }
            }
        } catch (error) {
            console.error('Error checking auth configuration:', error);
            alert(error?.message || 'Cannot connect to backend. Please make sure the backend is reachable (via proxy on :8760/api or directly on :8000).');
            return;
        }

        let loginUrl = `${base}/auth/google/login`;
        if (nextPath && typeof nextPath === 'string' && nextPath.startsWith('/') && !nextPath.startsWith('//')) {
            loginUrl += `?next=${encodeURIComponent(nextPath)}`;
        }
        window.location.href = loginUrl;
    }, []);

    const logout = useCallback(async () => {
        const base = get_base_url(window.location.origin);
        try {
            await fetch(`${base}/auth/logout`, { method: 'POST', credentials: 'include' });
        } finally {
            // Even when the backend is unreachable the local session state is
            // stale: drop it (and any auth error) so /login renders cleanly.
            clearCachedAvailableProviders();
            setUser(null);
            setPlatformRole('member');
            setAuthError(null);
            // Hand the hop to the redirect effect so the boundary keeps its
            // loader up until /login is the current route; a plain
            // `navigate()` from the caller is a transition and would let the
            // route table render the current page once in between.
            requestRedirect('/login');
        }
    }, [requestRedirect]);

    // Phase 4: switch the active workspace (server re-issues cookie + persists last_workspace_id).
    const switchWorkspace = useCallback(async (workspaceId) => {
        const base = getAuthBaseUrl();
        const resp = await fetch(`${base}/workspaces/${workspaceId}/switch`, {
            method: 'POST',
            credentials: 'include',
        });
        if (!resp.ok) {
            const body = await resp.json().catch(() => ({}));
            throw new Error(body?.detail || 'Failed to switch workspace');
        }
        const data = await resp.json().catch(() => ({}));
        if (data?.workspace) setWorkspace(data.workspace);
        if (data?.role) setRole(data.role);
        return data;
    }, []);

    /**
     * Merge a ``PATCH /workspaces/{id}`` response into the session so the
     * header, avatar menu and workspace list reflect a renamed workspace or a
     * new logo without a reload.  Only the active workspace is replaced
     * wholesale; the membership list just picks up ``name``/``logo``.
     */
    const applyWorkspaceUpdate = useCallback((updated) => {
        if (!updated || !updated.id) return;
        setWorkspace((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
        setWorkspaces((prev) => prev.map((item) => {
            if (item.id !== updated.id) return item;
            const next = { ...item };
            if (updated.name !== undefined) next.name = updated.name;
            if (updated.logo !== undefined) next.logo = updated.logo;
            return next;
        }));
    }, []);

    const redirectPending = redirectTo !== null;

    const value = useMemo(
        () => ({
            user,
            loading,
            initialized,
            redirectPending,
            retryAuth: bootstrapAuth,
            authError,
            authMethod,
            role,
            platformRole,
            isAuthEnabled,
            isSaas,
            billingEnabled,
            stripePublishableKey,
            feedbackEnabled,
            customerIntentsEnabled,
            workspace,
            workspaces,
            plan,
            switchWorkspace,
            applyWorkspaceUpdate,
            refresh,
            loginWithGoogle,
            logout,
        }),
        [
            user,
            loading,
            initialized,
            redirectPending,
            bootstrapAuth,
            authError,
            authMethod,
            role,
            platformRole,
            isAuthEnabled,
            isSaas,
            billingEnabled,
            stripePublishableKey,
            feedbackEnabled,
            customerIntentsEnabled,
            workspace,
            workspaces,
            plan,
            switchWorkspace,
            applyWorkspaceUpdate,
            refresh,
            loginWithGoogle,
            logout,
        ]
    );
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export const AuthGate = ({ children }) => {
    const { user, loading, authError, isAuthEnabled } = useAuth();
    const location = useLocation();
    if (loading) return <AuthLoadingContent />;
    if (authError) return <AuthErrorContent message={authError} />;
    if (!user && isAuthEnabled) return <Navigate to="/login" replace state={{ from: location }} />;
    return children;
};

export const RequireAdmin = ({ children }) => {
    const { loading, authError, role, isAuthEnabled } = useAuth();
    const location = useLocation();
    if (loading) return null;
    if (authError) {
        return <AuthErrorContent message={authError} />;
    }
    if (!isAuthEnabled) return children;
    if (role === 'Admin') return children;
    return <Navigate to="/" replace state={{ from: location }} />;
};

// Phase 4: guards routes that only Gods (platform-level super-admins) may access.
export const RequireGod = ({ children }) => {
    const { loading, authError, platformRole, isAuthEnabled } = useAuth();
    const location = useLocation();
    if (loading) return null;
    if (authError) {
        return <AuthErrorContent message={authError} />;
    }
    if (!isAuthEnabled) return children;
    if (platformRole === 'god') return children;
    return <Navigate to="/" replace state={{ from: location }} />;
};

/**
 * Guards the waiting-list routes (`/waitlist`, `/waitlist/apply`): they only
 * exist on SaaS, so a self-hosted install sends the visitor to `/login`
 * instead of rendering a form whose backend answers 404.
 */
export const RequireSaas = ({ children }) => {
    const { loading, authError, isSaas } = useAuth();
    if (loading) return <AuthLoadingContent />;
    if (authError) return <AuthErrorContent message={authError} />;
    if (isSaas !== true) return <Navigate to="/login" replace />;
    return children;
};
